import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ViewContainerRef,
  Output,
  EventEmitter, OnDestroy, HostListener, Inject, AfterViewInit, ComponentRef, ChangeDetectorRef
} from '@angular/core';
import {Subject, Observable, Subscription, firstValueFrom, isObservable} from 'rxjs';
import {ModalService} from './modal.service';
import {ModalInterface} from './modal.interface';
import {ModalDirective} from './directives/modal.directive';
import {ModalItemModel} from './modal-item.model';
import {ModalState} from './modal-state.enum';
import {tap} from 'rxjs/operators';
import {ModalStateEvent} from './modal-state-event';
import {Dialog} from 'primeng/dialog';
import {TranslateService} from '@ngx-translate/core';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock';
import {DOCUMENT} from '@angular/common';
import {LoaderService} from '../../core/services/loader.service';
import {AbstractComponent} from "../../core/components/abstract-component";

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss']
})
export class ModalComponent extends AbstractComponent implements OnInit, AfterViewInit, OnDestroy {
  get isHeaderVisible(): boolean {
    return this._isHeaderVisible;
  }

  set isHeaderVisible(value: boolean) {
    this._isHeaderVisible = value;
  }

  get internalState(): Observable<ModalStateEvent> {
    return this._internalState.pipe(tap((modalState: ModalStateEvent) => {
      this.modalStateEvent = modalState;
    }));
  }

  get hasButtons(): boolean {
    return this._hasButtons;
  }

  set hasButtons(value: boolean) {
    this._hasButtons = value;
  }

  get btnSuccessName(): string {
    return this.ms.btnSuccessName;
  }

  set btnSuccessName(value: string) {
    this.ms.btnSuccessName = value ?? this.translate.instant('shared.modal.modal_component.button.proceed');
  }

  get styles(): { slideFrom: string; modalType: string; size: string, light?: boolean, mobileFullscreen?: boolean } {
    return this.ms.styles || this._styles;
  }

  /**
   * PrimeNG moves an appended dialog below <body>, where component-scoped CSS
   * cannot reliably override the inline centering rules it applies. Keep the
   * viewport geometry on the dialog itself for page-like mobile flows.
   */
  get isMobileFullscreenViewport(): boolean {
    return !!this.styles.mobileFullscreen
      && !!this.document.defaultView?.matchMedia('(max-width: 1023px)').matches;
  }

  get dialogStyle(): {[key: string]: string} {
    if (this.isMobileFullscreenViewport) {
      return {
        position: 'fixed',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        height: '100dvh',
        minHeight: '100dvh',
        maxHeight: '100dvh',
        margin: '0',
        transform: 'none',
        boxSizing: 'border-box',
      };
    }

    return {minWidth: '20vw'};
  }

  @Input() set styles(value: { slideFrom: string; modalType: string; size: string, light?: boolean, mobileFullscreen?: boolean }) {
    this._styles = value;
  }

  @Input() component: ModalItemModel;
  @Output() state: EventEmitter<ModalStateEvent> = new EventEmitter<ModalStateEvent>();
  @ViewChild(Dialog) public contentModal: Dialog;
  @ViewChild(ModalDirective) appModal: ModalDirective;
  public name: string;
  private _isHeaderVisible: boolean = true;
  public spinnerState: boolean = false;
  public btnDisabled: boolean = false;
  public loading: boolean = false;
  private viewContainerRef: ViewContainerRef;
  public modalChildComponentRef: ComponentRef<ModalInterface>;
  public modalSubject: Subject<ModalStateEvent>;
  private _internalState: Observable<ModalStateEvent>;
  private _hasButtons: boolean = true;
  public hasResetButton: boolean = false;
  public hasBackButton: boolean = false;
  /** Optional navigation control in the modal header for full-screen flows. */
  public hasHeaderBackButton: boolean = false;
  public disableHeaderBackButton: boolean = false;
  public headerBackCallback: Function = () => {};
  /** Lets a guarded flow resolve its own close decision before dismissal. */
  public headerCloseCallback: Function | null = null;
  public hasDismissButton: boolean = true;
  public hasProceedButton: boolean = true;
  public disableBackButton: boolean = false;
  public disableDismissButton: boolean = false;
  public proceedButtonBadge: string = null;
  public resetButtonBadge: string = null;
  public backButtonLabel: string = null;
  public backCallback: Function = () => {};
  public progress: number = null;
  public visible = false;
  public modalStateEvent: ModalStateEvent = new ModalStateEvent(ModalState.INIT);
  public resetCallback: Function = (event: Event) => {
  };
  private _styles: { slideFrom: string, modalType: string, size: string, light?: boolean, mobileFullscreen?: boolean } = {
    slideFrom: 'top',
    modalType: 'modal-top',
    size: 'modal-fluid',
    light: true
  };
  public rendered: boolean;
  private closeGuardPending: boolean = false;
  private activeSessionId = 0;
  private finalizedSessionId: number | null = null;
  /** Hide events can arrive after a replacement modal has already opened. */
  private readonly pendingDialogHideSessions: number[] = [];
  private internalStateSubscription: Subscription | null = null;
  /** PrimeNG can emit onShow during the first host change-detection pass. */
  private dynamicChildReadyForShow = false;
  private dialogShowPending = false;
  private dynamicChildShown = false;

  get isCloseGuardedModal(): boolean {
    return typeof this.modalChildComponentRef?.instance?.beforeModalCloseAttempt === 'function';
  }

  constructor(
    public ms: ModalService,
    private translate: TranslateService,
    private ls: LoaderService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
  }

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    const currentCode = Number(event.code);
    let currentKey = event.key;
    if (!currentKey) {
      currentKey = String.fromCharCode(currentCode);
    }
    if (currentKey === 'Escape') {
      this.enableBodyScroll();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.visible) {
      return;
    }
    this.applyResponsiveDialogMode();
  }

  public ngOnInit(): void {
    this.ms.modalInstance = this;
    this.modalSubject = new Subject<ModalStateEvent>();
    this._internalState = this.modalSubject.asObservable();
  }

  ngAfterViewInit(): void {
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.finalizeActiveSession(false, true);
    this.enableBodyScroll();
  }

  public show(): void {
    this.ms.modalInstance = this;
    // Replacement must release the old dynamic tree immediately. Its later
    // PrimeNG onHide event is associated with the old session and must never
    // reset the modal that is about to be created below.
    this.finalizeActiveSession(true, false);
    this.activeSessionId += 1;
    this.finalizedSessionId = null;
    this.modalSubject = new Subject<ModalStateEvent>();
    this._internalState = this.modalSubject.asObservable();
    this.hasHeaderBackButton = false;
    this.disableHeaderBackButton = false;
    this.headerBackCallback = () => {};
    this.headerCloseCallback = null;
    this.dynamicChildReadyForShow = false;
    this.dialogShowPending = false;
    this.dynamicChildShown = false;
    this.watchInternalState();
    this.modalSubject.next(new ModalStateEvent(ModalState.INIT));
    this.visible = true;
    this.cdr.detectChanges();
    this.loadComponent();
    this.dynamicChildReadyForShow = true;
    // The dialog host is now rendered and the child has completed its first
    // lifecycle pass. PrimeNG's onShow is animation-dependent, so it cannot
    // be the sole delivery mechanism for the child session start.
    this.notifyDynamicChildShown();
    this.disableBodyScroll();
    this.applyResponsiveDialogMode();
    this.timeoutIds.push(setTimeout(() => {
      this.dialogHeight();
    }, 100));
  }

  private applyResponsiveDialogMode(): void {
    if (!this.contentModal) {
      return;
    }

    // The wizard supplies its own full-viewport geometry through dialogStyle.
    // Do not also switch PrimeNG to maximized mode: it retains stale
    // center/width values when an appended dialog is moved below <body>.
    if (this.styles.mobileFullscreen) {
      if (this.contentModal.maximized) {
        this.contentModal.maximized = false;
        this.cdr.detectChanges();
      }
      return;
    }

    const shouldBeFullscreen = (this.document.defaultView?.innerWidth ?? 1024) < 1024;
    if (this.contentModal.maximized !== shouldBeFullscreen) {
      this.contentModal.maximized = shouldBeFullscreen;
      this.cdr.detectChanges();
    }
  }

  public requestHide(force: boolean = false): void {
    if (force) {
      this.closeActiveSession();
      return;
    }

    if (this.closeGuardPending) {
      return;
    }

    this.runCloseGuard().then((canClose) => {
      if (!canClose) {
        return;
      }
      this.closeActiveSession();
    });
  }

  public onDialogHide(): void {
    const closedSessionId = this.pendingDialogHideSessions.shift();
    if (closedSessionId !== undefined) {
      // Resources were released at requestHide(). A delayed event from a
      // replaced dialog is visual-only and must not touch the live session.
      if (closedSessionId === this.activeSessionId) {
        this.resetClosedDialogState();
      }
      return;
    }

    if (this.closeGuardPending) {
      return;
    }

    this.visible = true;
    this.runCloseGuard().then((canClose) => {
      if (!canClose) {
        return;
      }
      this.closeActiveSession();
    });
  }

  public onDialogShow(): void {
    if (!this.dynamicChildReadyForShow) {
      this.dialogShowPending = true;
      return;
    }
    this.notifyDynamicChildShown();
  }

  public hide(): void {
    this.requestHide(true);
  }

  private closeActiveSession(): void {
    if (this.finalizedSessionId === this.activeSessionId) {
      this.visible = false;
      return;
    }
    const closingSessionId = this.activeSessionId;
    this.finalizeActiveSession(true, true);
    this.pendingDialogHideSessions.push(closingSessionId);
    this.visible = false;
  }

  /** Releases one logical modal session; safe for force-close and late events. */
  private finalizeActiveSession(emitHide: boolean, resetConfiguration: boolean): void {
    if (this.finalizedSessionId === this.activeSessionId) {
      return;
    }
    this.finalizedSessionId = this.activeSessionId;
    this.internalStateSubscription?.unsubscribe();
    this.internalStateSubscription = null;
    if (emitHide && !this.modalSubject?.closed) {
      this.modalSubject.next(new ModalStateEvent(ModalState.HIDE));
    }
    this.modalSubject?.complete();
    this.destroyModalChild();
    if (resetConfiguration) {
      this.resetClosedDialogState();
    }
  }

  private resetClosedDialogState(): void {
    this.ms.isVisible = false;
    this.visible = false;
    this.rendered = false;
    this.progress = null;
    this.loading = false;
    this.proceedButtonBadge = null;
    this.resetButtonBadge = null;
    this.disableBackButton = false;
    this.disableDismissButton = false;
    this.hasHeaderBackButton = false;
    this.disableHeaderBackButton = false;
    this.headerBackCallback = () => {};
    this.headerCloseCallback = null;
    this.isHeaderVisible = true;
    this.component = null;
    this.modalStateEvent = new ModalStateEvent(ModalState.INIT);
    this.ms.content = null;
    this.ms.selectedComponent = null;
    this.btnSuccessName = this.translate.instant('shared.modal.modal_component.button.proceed');
    this.enableBodyScroll();
    this.timeoutIds.forEach(e => clearTimeout(e));
  }

  public emitState(state: ModalStateEvent): void {
    this.modalSubject.next(state);
  }

  public updateName(value: string): void {
    this.name = value;
    this.cdr.detectChanges();
  }

  public removeStyles(): void {
    this.ms.styles = undefined;
  }

  public resetAction(): void {
    this.modalStateEvent.status = ModalState.PENDING;
    this.modalSubject.next(this.modalStateEvent);
  }

  public processAction(): void {
    this.modalStateEvent.status = ModalState.PENDING;
    this.modalSubject.next(this.modalStateEvent);
  }

  private emitSuccess(): void {
    this.modalStateEvent.status = ModalState.SUCCESS;
    this.state.emit(this.modalStateEvent);
    this.visible = false;
    this.enableBodyScroll();
  }

  private loadComponent(): void {
    const modalChild = this.component;
    // const componentFactory = this.componentFactoryResolver.resolveComponentFactory(modalChild.component);
    this.viewContainerRef = this.appModal.viewContainerRef;
    this.destroyModalChild();

    this.modalChildComponentRef = this.viewContainerRef.createComponent(modalChild.component);
    const componentRefInstance = this.modalChildComponentRef.instance;
    componentRefInstance.data = modalChild.data;
    componentRefInstance.parent = this;
    componentRefInstance.state = this._internalState;
    componentRefInstance.subject = this.modalSubject;
    // Dynamic modal children receive their contract after createComponent().
    // Run their first change-detection pass only after those values exist, so
    // lifecycle hooks cannot race PrimeNG's onShow event.
    this.modalChildComponentRef.changeDetectorRef.detectChanges();
  }

  private notifyDynamicChildShown(): void {
    if (this.dynamicChildShown) {
      return;
    }
    this.dialogShowPending = false;
    this.dynamicChildShown = true;
    this.modalChildComponentRef?.instance?.onModalShown?.();
  }

  private destroyModalChild(): void {
    const modalChild = this.modalChildComponentRef;
    this.modalChildComponentRef = null;
    try {
      modalChild?.instance?.releaseModalResources?.();
    } catch {
      // Resource cleanup is best-effort; the dynamic view must still die.
    }
    try {
      modalChild?.destroy();
    } catch {
      // ViewContainerRef.clear() may already have disposed a replaced child.
    }
    this.viewContainerRef?.clear();
  }

  private async runCloseGuard(): Promise<boolean> {
    const hook = this.modalChildComponentRef?.instance?.beforeModalCloseAttempt;
    if (typeof hook !== 'function') {
      return true;
    }

    this.closeGuardPending = true;
    try {
      const decision = hook.call(this.modalChildComponentRef.instance);
      if (isObservable(decision)) {
        return !!(await firstValueFrom(decision));
      }
      return !!(await Promise.resolve(decision));
    } finally {
      this.closeGuardPending = false;
    }
  }

  private watchInternalState(): void {
    const subscription = this._internalState.subscribe((value: ModalStateEvent) => {
      switch (value.status) {
        case ModalState.PROCESS:
          this.loading = true;
          break;
        case ModalState.PROCESS_END:
          this.loading = false;
          break;
        case ModalState.INIT:
        case ModalState.VALID:
          this.btnDisabled = false;
          this.loading = false;
          break;
        case ModalState.PENDING:
          this.btnDisabled = true;
          this.loading = true;
          break;
        case ModalState.SUCCESS:
          this.loading = false;
          this.emitSuccess();
          subscription.unsubscribe();
          if (this.internalStateSubscription === subscription) {
            this.internalStateSubscription = null;
          }
          break;
        case ModalState.INVALID:
          this.btnDisabled = true;
          this.loading = false;
          break;
      }
    });
    this.internalStateSubscription = subscription;
  }

  disableBodyScroll(): void {
    disableBodyScroll(this.document.body, { allowTouchMove: () => true });
  }

  enableBodyScroll(): void {
    enableBodyScroll(this.document.body);
  }

  dialogHeight(): number {
    const nodeList = this.document.querySelectorAll('.app-modal');

    if (nodeList.length && nodeList.item(0).clientHeight) {
      this.rendered = true;
      return nodeList.item(0).clientHeight;
    }
    return 0;
  }

  emitReset(event: Event): void {
    this.emitState(new ModalStateEvent(ModalState.RESET));
  }
}
